import React, { useId, useState } from "react";
import { Box, Menu, MenuItem, SxProps, Theme, Tooltip, Typography, useTheme } from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { ExitToApp } from "@mui/icons-material";
import Button from "./Button";

export interface ActionItem {
  label: string;
  icon?: JSX.Element;
  color?: "inherit" | "primary" | "secondary" | "success" | "error" | "warning" | "info";
  buttonColor?: string;
  tooltipMessage?: string;
  onClick?: () => void;
  isHidden?: boolean;
  disabled?: boolean;
}

interface ActionMenuProps {
  label?: string;
  actions: ActionItem[];
  disabled?: boolean;
  sx?: SxProps<Theme>;
  removeUpperCase?: boolean;
}

export default function ActionMenu({ label = "Action", actions, disabled = false, sx, removeUpperCase }: ActionMenuProps) {
  const theme = useTheme();
  const instanceId = useId();
  const triggerId = `action-menu-trigger-${instanceId}`;
  const menuListId = `action-menu-list-${instanceId}`;
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElMenu(null);
  };

  const handleActionClick = (action: ActionItem) => {
    if (action.disabled) return;
    handleMenuClose();
    action.onClick && action.onClick();
  };

  const open = Boolean(anchorElMenu);

  return (
    <>
      <Button
        id={triggerId}
        aria-controls={open ? menuListId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="outlined"
        color="info"
        disableElevation
        disabled={disabled}
        onClick={handleMenuClick}
        removeUpperCase={removeUpperCase}
        sx={sx}
        startIcon={<ExitToApp />}
        endIcon={
          <Box
            component="span"
            sx={{
              display: "flex",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              padding: "2px",
            }}
          >
            <KeyboardArrowDown />
          </Box>
        }
      >
        {label}
      </Button>

      <Menu
        id={menuListId}
        MenuListProps={{ "aria-labelledby": triggerId, sx: { py: 0.7 } }}
        anchorEl={anchorElMenu}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.6,
            minWidth: 200,
            borderRadius: 2.5,
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(148,163,184,0.18)" : "rgba(148,163,184,0.28)"}`,
            backgroundColor: theme.palette.mode === "dark" ? "#1f2937" : "#ffffff",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 14px 36px rgba(2,6,23,0.55)"
                : "0 14px 32px rgba(15,23,42,0.12)",
            overflow: "hidden",
          },
        }}
      >
        {actions
          .filter((action) => !action.isHidden)
          .map((action, index) => {
            const getColor = () => {
              if (action.buttonColor) {
                return action.buttonColor;
              }
              if (!action.color || action.color === "inherit") {
                return "inherit";
              }
              return theme.palette[action.color].main;
            };

            const isGradient = action.buttonColor?.includes("gradient");

            const menuItem = (
              <MenuItem
                disabled={action.disabled}
                onClick={() => handleActionClick(action)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 1.5,
                  mx: 0.75,
                  my: 0.35,
                  px: 1.1,
                  py: 0.75,
                  gap: 1.25,
                  transition: "all 0.2s ease",
                  opacity: action.disabled ? 0.5 : 1,
                  cursor: action.disabled ? "not-allowed" : "pointer",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.08)",
                  },
                }}
              >
                {action.icon && (
                  <Box
                    sx={{
                      border: isGradient ? 0 : 1,
                      borderColor: isGradient ? "transparent" : getColor(),
                      borderRadius: 1,
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: isGradient ? "white" : getColor(),
                      background: isGradient ? getColor() : "transparent",
                    }}
                  >
                    {action.icon}
                  </Box>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: theme.palette.mode === "dark" ? "#f8fafc" : "#0f172a",
                  }}
                >
                  {action.label}
                </Typography>
              </MenuItem>
            );

            return (
              <React.Fragment key={`${action.label}-${index}`}>
                {action.tooltipMessage ? (
                  <Tooltip title={action.tooltipMessage} placement="left" arrow>
                    {menuItem}
                  </Tooltip>
                ) : (
                  menuItem
                )}
              </React.Fragment>
            );
          })}
      </Menu>
    </>
  );
}
